import { scoreMeta, tallyText, type LeaseScorecard, type ScorecardRowDef } from './leaseSearch';

/**
 * One building against the tenant's spec: a line per row the contract declares, in the contract's order - what the
 * building has, and whether that meets his number. Rendered only for a building that IS scored (an off-market
 * target has nothing to score, and an empty box would read as five unknowns).
 *
 * The threshold is the row's `title` rather than a sixth column: his requirement is printed whole at the top of the
 * page, and repeating five thresholds on every card would bury what differs between the buildings.
 */
export default function SpecScorecard({
  rows,
  scorecard,
  unknownTally,
}: {
  rows: ScorecardRowDef[];
  scorecard: LeaseScorecard | null;
  /** The kind's word for an unknown row in the tally (`KindWords.unknownTally`); "not stated" when absent. */
  unknownTally?: string;
}) {
  const floors = scorecard?.floors;
  if (!floors) return null;
  const printed = rows.filter((r) => floors[r.key]);
  if (!printed.length) return null;
  const tally = tallyText(scorecard?.counts, unknownTally);
  return (
    <div className="portal-score">
      <p className="portal-score-head">Against your spec</p>
      <ul className="portal-score-rows">
        {printed.map((r) => {
          const f = floors[r.key]!;
          const meta = scoreMeta(f.status);
          return (
            <li
              key={r.key}
              data-score-row={r.key}
              className={`portal-score-row${meta ? ` ${meta.className}` : ''}`}
              title={f.threshold ? `Your spec: ${f.threshold}` : undefined}
            >
              <span className="portal-score-label">{f.label ?? r.label}</span>
              <span className="portal-score-actual">{f.actual}</span>
              {meta && (
                <span className="portal-score-mark">
                  {meta.mark && <span aria-hidden="true">{meta.mark} </span>}
                  {/* An unknown row's own words already say it ("Not stated in the listing"); the status word is
                      there for a screen reader, not repeated beside it. */}
                  {f.status === 'UNKNOWN' ? <span className="portal-sr-only">{meta.word}</span> : meta.word}
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {tally && (
        <p className="portal-score-tally" data-testid="portal-score-tally">
          {tally}
        </p>
      )}
    </div>
  );
}
